import { apiFetch } from "../api.js";
import type {
  CapitalGainsPreviewResponse,
  CreateTaxDocumentInput,
  Hsa8889Response,
  TaxDocument,
  TaxDocumentsListResponse,
  TaxProjection,
  TaxProjectionsListResponse,
  UpdateTaxDocumentInput,
} from "./types.js";

/**
 * Phase 7 Session 1 — tax CRUD + read client. Wraps `apiFetch` against
 * the TaxFn Lambdalith in `Hearth-Dev-Api-Cashflow`.
 *
 * Three root paths via the Lambdalith proxy convention:
 *   - /v1/tax/{capital-gains, hsa-8889/{tax_year}}  — read math
 *   - /v1/tax-documents                              — CRUD
 *   - /v1/tax-projections                            — read (job writes)
 */

// === capital gains + wash sale ============================================

export function getCapitalGainsPreview(
  asOf?: string,
): Promise<CapitalGainsPreviewResponse> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  return apiFetch<CapitalGainsPreviewResponse>(`/v1/tax/capital-gains${qs}`);
}

// === HSA Form 8889 prep ===================================================

export function getHsa8889(
  taxYear: string | number,
  args?: {
    coverage?: "self_only" | "family";
    age?: number;
    employer_contributions_cents?: number;
    qualified_funding_distribution_cents?: number;
    age_65_plus_or_disabled?: boolean;
  },
): Promise<Hsa8889Response> {
  const qs = new URLSearchParams();
  if (args?.coverage) qs.set("coverage", args.coverage);
  if (args?.age !== undefined) qs.set("age", String(args.age));
  if (args?.employer_contributions_cents !== undefined)
    qs.set(
      "employer_contributions_cents",
      String(args.employer_contributions_cents),
    );
  if (args?.qualified_funding_distribution_cents !== undefined)
    qs.set(
      "qualified_funding_distribution_cents",
      String(args.qualified_funding_distribution_cents),
    );
  if (args?.age_65_plus_or_disabled === true)
    qs.set("age_65_plus_or_disabled", "true");
  const qsStr = qs.toString();
  return apiFetch<Hsa8889Response>(
    `/v1/tax/hsa-8889/${encodeURIComponent(String(taxYear))}${qsStr ? `?${qsStr}` : ""}`,
  );
}

// === tax_documents CRUD ===================================================

export function listTaxDocuments(args?: {
  year?: string;
  includeArchived?: boolean;
}): Promise<TaxDocumentsListResponse> {
  const qs = new URLSearchParams();
  if (args?.year) qs.set("year", args.year);
  if (args?.includeArchived === true) qs.set("include_archived", "true");
  const qsStr = qs.toString();
  return apiFetch<TaxDocumentsListResponse>(
    `/v1/tax-documents${qsStr ? `?${qsStr}` : ""}`,
  );
}

export function createTaxDocument(
  input: CreateTaxDocumentInput,
): Promise<TaxDocument> {
  return apiFetch<TaxDocument>(`/v1/tax-documents`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getTaxDocument(
  year: string,
  docId: string,
): Promise<TaxDocument> {
  return apiFetch<TaxDocument>(
    `/v1/tax-documents/${encodeURIComponent(year)}/${encodeURIComponent(docId)}`,
  );
}

export function updateTaxDocument(
  year: string,
  docId: string,
  input: UpdateTaxDocumentInput,
): Promise<TaxDocument> {
  return apiFetch<TaxDocument>(
    `/v1/tax-documents/${encodeURIComponent(year)}/${encodeURIComponent(docId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteTaxDocument(
  year: string,
  docId: string,
): Promise<void> {
  return apiFetch<void>(
    `/v1/tax-documents/${encodeURIComponent(year)}/${encodeURIComponent(docId)}`,
    {
      method: "DELETE",
    },
  );
}

// === tax_projections read =================================================

export function listTaxProjections(
  year?: string,
): Promise<TaxProjectionsListResponse> {
  const qs = year ? `?year=${encodeURIComponent(year)}` : "";
  return apiFetch<TaxProjectionsListResponse>(`/v1/tax-projections${qs}`);
}

export type {
  CapitalGainsPreviewResponse,
  Hsa8889Response,
  TaxDocument,
  TaxDocumentsListResponse,
  TaxProjection,
  TaxProjectionsListResponse,
  CreateTaxDocumentInput,
  UpdateTaxDocumentInput,
};
